/**
 * Initializes the 8 bootstrap collection stores.
 *
 * Server: fetches /api/bootstrap once and hydrates only what the route renders
 * (see app/utils/bootstrapPayload.ts); that state reaches the client through
 * the serialized Pinia state.
 *
 * Client:
 *   - Payload carries data (normal SSR page): Pinia has already restored it.
 *     Anything the server left out is loaded after hydration, off the critical
 *     path, and a global route middleware makes sure a page that needs it does
 *     not render before it arrives.
 *   - Payload carries NO data: the prerendered /offline page (which the service
 *     worker also serves as the offline fallback for any navigation), an SSR
 *     render whose bootstrap fetch failed, or no SSR at all. Load the bootstrap
 *     before the app renders — from the network, which the service worker
 *     answers from its 'ohc-bootstrap' cache when offline, or straight from
 *     that cache when the service worker is not controlling the page yet.
 *
 * The client path deliberately ignores the 'stores-initialized' flag carried
 * in the payload. It is serialized as `true` by every server render,
 * including /offline's prerender with an empty bootstrap — which used to make
 * the client skip loading entirely, so "Back to Home" from /offline showed an
 * empty site list.
 */

import { useSitesStore } from '@/stores/sites';
import { useNeighborhoodsStore } from '@/stores/neighborhoods';
import { usePointsOfInterestStore } from '@/stores/points-of-interest';
import { useToursStore } from '@/stores/tours';
import { useTrailsStore } from '@/stores/trails';
import { useEventsStore } from '@/stores/events';
import { useSponsorsStore } from '@/stores/sponsors';
import { usePartnersStore } from '@/stores/partners';
import { hydrateCollectionStore } from '@/utils/defineCollectionStore';
import { ssrSubset, isUsableBootstrap, readCachedBootstrap, staleCollectionKeys, missingKeys, requiredKeysForPath } from '@/utils/bootstrapPayload';

// Resolved lazily (not destructured at module scope) so it is always the
// Nuxt-configured global by the time it is called.
const fetchBootstrap = (): Promise<any> => (globalThis as any).$fetch('/api/bootstrap', { timeout: 10_000 });

export default defineNuxtPlugin(async (nuxtApp: any) => {
    // Mark stores as initializing to prevent duplicate fetches (server only;
    // see the header comment for why the client ignores these).
    const isInitializing = useState('stores-initializing', () => false);
    const isInitialized = useState('stores-initialized', () => false);
    // Aggregate bootstrap version (SHA-256 of the 8 collections' hashes), used for `?since=`.
    const bootstrapVersion = useState<string | null>('bootstrap-version', () => null);

    if (import.meta.server && (isInitializing.value || isInitialized.value)) {
        return;
    }

    if (import.meta.server) isInitializing.value = true;

    try {
        // Initialize stores
        const sitesStore = useSitesStore();
        const neighborhoodsStore = useNeighborhoodsStore();
        const eventsStore = useEventsStore();
        const toursStore = useToursStore();
        const trailsStore = useTrailsStore();
        const pointsOfInterestStore = usePointsOfInterestStore();
        const sponsorsStore = useSponsorsStore();
        const partnersStore = usePartnersStore();

        // Bootstrap collection key -> store instance. Keys match `/api/bootstrap`'s BOOTSTRAP_KEYS
        // (the KV collection keys), which for points-of-interest differs from the Pinia store id.
        const storesByKey: Record<string, any> = {
            sites: sitesStore,
            neighborhoods: neighborhoodsStore,
            events: eventsStore,
            tours: toursStore,
            trails: trailsStore,
            'points-of-interest': pointsOfInterestStore,
            sponsors: sponsorsStore,
            partners: partnersStore,
        };

        const hydrateAll = (collections: Record<string, any> = {}) => {
            for (const [key, store] of Object.entries(storesByKey)) {
                hydrateCollectionStore(store, collections[key]);
            }
        };

        if (nuxtApp.ssrContext) {
            // Server-side: one fetch for all 8 collections.
            console.log('🚀 SSR: Loading data from /api/bootstrap');

            const bootstrap: any = await (globalThis as any).$fetch('/api/bootstrap').catch(() => ({ collections: {}, version: null }));
            // Only what this route renders goes into the stores — and so into
            // the Pinia state that is serialized into __NUXT_DATA__. The client
            // loads the rest after hydration. See app/utils/bootstrapPayload.ts.
            const subset = ssrSubset(bootstrap?.collections, nuxtApp.ssrContext.url || '/');
            hydrateAll(subset.collections);
            bootstrapVersion.value = bootstrap?.version ?? null;

            // A small marker, not the data: the data already rides in the
            // serialized Pinia state. (This used to be the whole bootstrap
            // object, which nothing on the client read.)
            nuxtApp.payload.bootstrap = {
                version: bootstrap?.version ?? null,
                deferred: subset.deferred,
            };
        } else {
            // Client-side. Only collections whose store is missing or out of
            // date get re-hydrated, so a full load after an SSR page does not
            // churn the (identical) site list the page is already showing.
            const applyBootstrap = (bootstrap: any) => {
                const keys = staleCollectionKeys(storesByKey, bootstrap.collections);
                for (const key of keys) hydrateCollectionStore(storesByKey[key], bootstrap.collections[key]);
                bootstrapVersion.value = bootstrap.version ?? null;
            };

            const loadBootstrap = async (): Promise<any | null> => {
                try {
                    const fresh = await fetchBootstrap();
                    if (isUsableBootstrap(fresh)) return fresh;
                } catch (error) {
                    console.warn('Bootstrap fetch failed, trying the offline cache', error);
                }
                return readCachedBootstrap();
            };

            // One in-flight load at a time; cleared on failure so the next
            // caller (route middleware, a later navigation) can try again.
            let pending: Promise<boolean> | null = null;
            const ensureFullBootstrap = () => {
                pending ??= loadBootstrap().then((bootstrap) => {
                    if (!bootstrap) {
                        pending = null;
                        return false;
                    }
                    applyBootstrap(bootstrap);
                    return true;
                });
                return pending;
            };

            // The service worker serves the prerendered /offline page for any
            // navigation it cannot answer (sw.ts). Nuxt then hydrates it as
            // /offline and rewrites the address bar, so capture what was
            // actually asked for now, while window.location still has it;
            // pages/offline.vue re-opens it from cached data where it can.
            if (nuxtApp.payload.path === '/offline' && window.location.pathname !== '/offline') {
                useState<string | null>('offline-requested-url').value = window.location.pathname + window.location.search + window.location.hash;
            }

            const marker = nuxtApp.payload.bootstrap;
            if (!marker?.version) {
                // No data came with the page (see header comment). Block until
                // we have some: the app has nothing to render otherwise.
                console.log('🌐 Client: no bootstrap in payload, loading it');
                await ensureFullBootstrap();
            } else if (Array.isArray(marker.deferred) && marker.deferred.length) {
                // After hydration and idle, so it competes with nothing the
                // first paint needs. This also warms the SW's offline copy.
                nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
                    const run = () => ensureFullBootstrap();
                    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
                    else setTimeout(run, 1000);
                });
            }

            // A client-side navigation into a page that reads a collection
            // the server left out waits for it. Tours/single.vue reads
            // trailsData once at setup, non-reactively, so it cannot just
            // render first and fill in later.
            addRouteMiddleware(
                'ohc-bootstrap-ready',
                async (to: { path: string }) => {
                    const needed = missingKeys(storesByKey, requiredKeysForPath(to.path));
                    if (!needed.length) return;
                    await ensureFullBootstrap();
                    // Still missing (offline with nothing cached): render with
                    // empty data rather than crash the page on a null.
                    for (const key of missingKeys(storesByKey, needed)) {
                        hydrateCollectionStore(storesByKey[key], { data: [], meta: { version: null } });
                        // hydrateCollectionStore records version null, which
                        // keeps the key "missing" so the next load retries it.
                    }
                },
                { global: true }
            );
        }

        // Initialize archive states (both server and client)
        neighborhoodsStore.updateNeighborhoods?.(false);
        sitesStore.updateSites?.(false);
        eventsStore.toggleArchivedEvents?.(false);
        partnersStore.toggleArchivedPartners?.(false);
        toursStore.toggleArchivedTours?.(false);
        sponsorsStore.toggleArchivedSponsors?.(false);
        pointsOfInterestStore.toggleArchivedPoints?.(false);
        trailsStore.toggleArchivedTrails?.(false);

        if (import.meta.server) isInitialized.value = true;
    } catch (error) {
        console.error('Failed to initialize stores:', error);
    } finally {
        if (import.meta.server) isInitializing.value = false;
    }
});
